import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useAddTravelToAssignment } from '@/hooks/use-crew';
import { handleApiError } from '@/lib/api-client';
import { toIsoFromDatetimeLocal } from '@/lib/date-utils';
import type { TravelType, CreateTravelRecordPayload } from '@/lib/types';

interface AddTravelDialogProps {
  assignmentId: number;
  techId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddTravelDialog({ assignmentId, techId: _techId, open, onOpenChange }: AddTravelDialogProps) {
  const addTravel = useAddTravelToAssignment();
  const [formError, setFormError] = useState<string | null>(null);
  const [travelType, setTravelType] = useState<TravelType>('flight');

  // Common fields
  const [description, setDescription] = useState('');
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');

  // Flight fields
  const [airline, setAirline] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [departureAirport, setDepartureAirport] = useState('');
  const [arrivalAirport, setArrivalAirport] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [seat, setSeat] = useState('');
  const [bookingReference, setBookingReference] = useState('');

  // Car rental fields
  const [rentalCompany, setRentalCompany] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [dropoffLocation, setDropoffLocation] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dailyRate, setDailyRate] = useState('');

  // Hotel fields
  const [hotelName, setHotelName] = useState('');
  const [hotelAddress, setHotelAddress] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkOutDate, setCheckOutDate] = useState('');
  const [hotelConfirmation, setHotelConfirmation] = useState('');
  const [nightlyRate, setNightlyRate] = useState('');

  const resetForm = () => {
    setTravelType('flight');
    setDescription('');
    setConfirmationNumber('');
    setDepartureDate('');
    setReturnDate('');
    setCost('');
    setNotes('');
    setAirline('');
    setFlightNumber('');
    setDepartureAirport('');
    setArrivalAirport('');
    setDepartureTime('');
    setArrivalTime('');
    setSeat('');
    setBookingReference('');
    setRentalCompany('');
    setVehicleType('');
    setPickupLocation('');
    setDropoffLocation('');
    setPickupDate('');
    setDropoffDate('');
    setDailyRate('');
    setHotelName('');
    setHotelAddress('');
    setCheckInDate('');
    setCheckOutDate('');
    setHotelConfirmation('');
    setNightlyRate('');
    setFormError(null);
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!description || !departureDate) {
      setFormError('Description and departure date are required.');
      return;
    }

    const payload: Omit<CreateTravelRecordPayload, 'tech' | 'assignment'> = {
      travel_type: travelType,
      description,
      confirmation_number: confirmationNumber || undefined,
      departure_date: departureDate,
      return_date: returnDate || undefined,
      cost: cost || undefined,
      notes: notes || undefined,
    };

    if (travelType === 'flight' && airline) {
      payload.flight_info = {
        airline,
        flight_number: flightNumber || '',
        departure_airport: departureAirport || '',
        arrival_airport: arrivalAirport || '',
        departure_time: toIsoFromDatetimeLocal(departureTime),
        arrival_time: toIsoFromDatetimeLocal(arrivalTime),
        seat: seat || undefined,
        booking_reference: bookingReference || undefined,
      };
    }

    if (travelType === 'car_rental' && rentalCompany) {
      payload.car_rental = {
        rental_company: rentalCompany,
        vehicle_type: vehicleType || undefined,
        pickup_location: pickupLocation || '',
        dropoff_location: dropoffLocation || '',
        pickup_date: toIsoFromDatetimeLocal(pickupDate),
        dropoff_date: toIsoFromDatetimeLocal(dropoffDate),
        confirmation_number: confirmationNumber || undefined,
        daily_rate: dailyRate || undefined,
      };
    }

    if (travelType === 'hotel' && hotelName) {
      payload.hotel_booking = {
        hotel_name: hotelName,
        address: hotelAddress || undefined,
        check_in_date: checkInDate || '',
        check_out_date: checkOutDate || '',
        confirmation_number: hotelConfirmation || undefined,
        nightly_rate: nightlyRate || undefined,
      };
    }

    try {
      await addTravel.mutateAsync({ assignmentId, data: payload });
      resetForm();
      onOpenChange(false);
    } catch (err) {
      setFormError(handleApiError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Travel Record</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {formError && (
            <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{formError}</div>
          )}

          {/* Travel Type */}
          <div>
            <Label>Travel Type</Label>
            <Select value={travelType} onValueChange={(v) => setTravelType(v as TravelType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="flight">Flight</SelectItem>
                <SelectItem value="car_rental">Car Rental</SelectItem>
                <SelectItem value="hotel">Hotel</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Common Fields */}
          <div>
            <Label>Description *</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Flight to Houston for bridge inspection"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Departure Date *</Label>
              <Input
                type="date"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Return Date</Label>
              <Input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Confirmation Number</Label>
              <Input
                value={confirmationNumber}
                onChange={(e) => setConfirmationNumber(e.target.value)}
                placeholder="e.g., ABC123"
              />
            </div>
            <div>
              <Label>Cost ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Flight Form */}
          {travelType === 'flight' && (
            <div className="border rounded-lg p-4 space-y-4 bg-blue-50/50">
              <h4 className="font-medium text-sm text-blue-800">Flight Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Airline</Label>
                  <Input
                    value={airline}
                    onChange={(e) => setAirline(e.target.value)}
                    placeholder="e.g., United Airlines"
                  />
                </div>
                <div>
                  <Label>Flight Number</Label>
                  <Input
                    value={flightNumber}
                    onChange={(e) => setFlightNumber(e.target.value)}
                    placeholder="e.g., UA1234"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Departure Airport</Label>
                  <Input
                    value={departureAirport}
                    onChange={(e) => setDepartureAirport(e.target.value)}
                    placeholder="e.g., LAX"
                  />
                </div>
                <div>
                  <Label>Arrival Airport</Label>
                  <Input
                    value={arrivalAirport}
                    onChange={(e) => setArrivalAirport(e.target.value)}
                    placeholder="e.g., IAH"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Departure Date/Time</Label>
                  <Input
                    type="datetime-local"
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Arrival Date/Time</Label>
                  <Input
                    type="datetime-local"
                    value={arrivalTime}
                    onChange={(e) => setArrivalTime(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Seat</Label>
                  <Input
                    value={seat}
                    onChange={(e) => setSeat(e.target.value)}
                    placeholder="e.g., 14A"
                  />
                </div>
                <div>
                  <Label>Booking Reference</Label>
                  <Input
                    value={bookingReference}
                    onChange={(e) => setBookingReference(e.target.value)}
                    placeholder="e.g., XYZABC"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Car Rental Form */}
          {travelType === 'car_rental' && (
            <div className="border rounded-lg p-4 space-y-4 bg-green-50/50">
              <h4 className="font-medium text-sm text-green-800">Car Rental Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rental Company</Label>
                  <Input
                    value={rentalCompany}
                    onChange={(e) => setRentalCompany(e.target.value)}
                    placeholder="e.g., Enterprise"
                  />
                </div>
                <div>
                  <Label>Vehicle Type</Label>
                  <Input
                    value={vehicleType}
                    onChange={(e) => setVehicleType(e.target.value)}
                    placeholder="e.g., SUV, Sedan"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Location</Label>
                  <Input
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    placeholder="e.g., IAH Airport"
                  />
                </div>
                <div>
                  <Label>Dropoff Location</Label>
                  <Input
                    value={dropoffLocation}
                    onChange={(e) => setDropoffLocation(e.target.value)}
                    placeholder="e.g., IAH Airport"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pickup Date/Time</Label>
                  <Input
                    type="datetime-local"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Dropoff Date/Time</Label>
                  <Input
                    type="datetime-local"
                    value={dropoffDate}
                    onChange={(e) => setDropoffDate(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Daily Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={dailyRate}
                  onChange={(e) => setDailyRate(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
          )}

          {/* Hotel Form */}
          {travelType === 'hotel' && (
            <div className="border rounded-lg p-4 space-y-4 bg-purple-50/50">
              <h4 className="font-medium text-sm text-purple-800">Hotel Details</h4>
              <div>
                <Label>Hotel Name</Label>
                <Input
                  value={hotelName}
                  onChange={(e) => setHotelName(e.target.value)}
                  placeholder="e.g., Marriott Houston"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  value={hotelAddress}
                  onChange={(e) => setHotelAddress(e.target.value)}
                  placeholder="Hotel address..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Check-in Date</Label>
                  <Input
                    type="date"
                    value={checkInDate}
                    onChange={(e) => setCheckInDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Check-out Date</Label>
                  <Input
                    type="date"
                    value={checkOutDate}
                    onChange={(e) => setCheckOutDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Confirmation Number</Label>
                  <Input
                    value={hotelConfirmation}
                    onChange={(e) => setHotelConfirmation(e.target.value)}
                    placeholder="e.g., HTL456"
                  />
                </div>
                <div>
                  <Label>Nightly Rate ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={nightlyRate}
                    onChange={(e) => setNightlyRate(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Other Form - just common fields + notes */}
          {travelType === 'other' && (
            <div className="border rounded-lg p-4 space-y-4 bg-gray-50/50">
              <h4 className="font-medium text-sm text-gray-800">Additional Details</h4>
              <p className="text-xs text-gray-500">
                Use the common fields above for dates and confirmation number.
              </p>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={addTravel.isPending}
            className="w-full"
          >
            {addTravel.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Travel Record
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
